/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { Select, Text, Title } from '@components/v2/ui';
import { Datatable } from '@components/v2/ui/Datatable/Datatable';
import { DefaultModal } from '@components/v2/ui/DefaultModal';
import { Pagination } from '@components/v2/ui/Pagination';
import {
	ActionIcon,
	Badge,
	Box,
	Center,
	Flex,
	Paper,
	Space,
} from '@mantine/core';
import useStore from '@store/index';
import { addFooterProductionsForMintColumn } from '@utils/datatable';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProductionsV2 } from '../../api/get-productionsV2';
import { TProduction } from '../../types';
import StepperHeader from './components/StepperHeader/StepperHeader';
import { tableSchemaBase as columnsReviews } from './mint-productions-review.columns copy';
import { tableSchemaBase as columns } from './mint-productions.columns';

import { useGetMeasurabilities } from '@api/get-measurabilities';
import { useGetProducers } from '@api/get-producers';
import { DatePickerInput } from '@components/v2/ui/DatePickerInput/DatePickerInput';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconFilter } from '@tabler/icons-react';
import {
	cloneDeep,
	convertArrayToLabelValue,
} from '@utils/functions';
import clsx from 'clsx';
import dayjs from 'dayjs';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import sum from 'lodash/sum';
import { useMintProduction } from '../../api/mint-production';
import { Details } from '../Details';
import { QueryParams } from '../Filter/Filter';
import classes from './MintProductions.module.css';
import { Filter } from './components/Filter';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

const MintProductions = () => {
	/* ******************************** STATE *********************************** */
	const [
		openedDetailsProduction,
		{ toggle: toggleDetailsProduction },
	] = useDisclosure();

	const [
		openedFilters,
		{ open: openFilters, close: closeFilters },
	] = useDisclosure(false);

	const [
		openedMint,
		{ toggle: toggleMint, close: closeMint },
	] = useDisclosure();

	const [
		openedSelectedProductions,
		{
			open: openSelectedProductions,
			close: closeSelectedProductions,
		},
	] = useDisclosure(false);

	const handleDeleteOneFromModal = (
		recordToDelete: TProduction
	) => {
		const newList = selectedProductionsForMint.filter(
			(item) => item.id !== recordToDelete.id
		);
		if (newList.length === 0) {
			closeSelectedProductions();
		}
		setSelectedProductionsForMint(newList);
	};

	const handleDeleteAllFromModal = () => {
		setSelectedProductionsForMint([]);
		closeSelectedProductions();
	};

	const [
		openedDeleteProduction,
		{
			toggle: toggleDeleteProduction,
			close: closeDeleteProduction,
		},
	] = useDisclosure();

	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);
	const [selectedProduction, setSelectedProduction] =
		useState<TProduction | null>(null);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [filtersNbr, setFiltersNbr] = useState(0);
	const [queryParams, setQueryParams] =
		useState<QueryParams>({});

	const {
		selectedProductionsForMint,
		setSelectedProductionsForMint,
		setIsMintProductions,
		currentStepMintProductions,
		setCurrentStepMintProductions,
		selectedProducerForProductionsMint,
		setSelectedProducerForProductionsMint,
		setSelectedProjectForProductionsMint,
		selectedProjectForProductionsMint,
	} = useStore();

	/* ******************************** HOOKS *********************************** */

	const { data: producers } = useGetProducers({
		queryConfig: {
			enabled: true,
		},
		params: {
			pageSize: 100,
		},
	});

	const { data: projects } = useGetMeasurabilities({
		queryConfig: {},
		params: { pageSize: 1000 },
	});

	const projectsFiltred =
		projects?.rows && Array.isArray(projects.rows)
			? projects.rows.filter(
					(project) =>
						project.entity ===
						selectedProducerForProductionsMint
				)
			: [];

	const handleApplyFilters = (filters: QueryParams) => {
		setQueryParams(filters);
		const siteCount =
			filters.site && filters.site.length > 0
				? filters.site.length
				: 0;
		setFiltersNbr(siteCount);
		setPage(1);
	};

	const { data, isFetching } = useProductionsV2({
		page: page,
		size: pageSize,
		queryParams: {
			verified: true,
			status: 'AVAILABLE',
			project: selectedProjectForProductionsMint,
			...queryParams,
		},
		config: {
			enabled: !!selectedProjectForProductionsMint,
		},
	});

	const mintProductionMutation = useMintProduction({
		mutationConfig: {
			onSuccess: () => {
				notifications.show({
					title: t('global.success'),
					message: t(
						'productions.notifications.productionSuccessfullyMinted'
					),
					color: 'var(--mantine-color-igluGreen-9)',
				});
				handleReset();
			},
			onError(error) {
				notifications.show({
					title: t('global.error'),
					message: t(`api.${error.type}`),
					color: 'var(--mantine-color-igluRed-9)',
				});
			},
		},
	});

	const { t } = useTranslation();

	/* ******************************** HANDLERS ******************************** */
	const handleProducerChange = (value: string | null) => {
		setSelectedProducerForProductionsMint(value);
		setSelectedProjectForProductionsMint(null);
		setSelectedProductionsForMint([]);

		setQueryParams({});
	};

	const handleProjectChange = (value: string | null) => {
		setSelectedProjectForProductionsMint(value);
		setSelectedProductionsForMint([]);
		const { startDate, site, ...rest } = queryParams;
		const newFilters = {
			...rest,
			project: value ? [value] : undefined,
		};

		if (!value) {
			delete newFilters.project;
		}

		setQueryParams(newFilters);
		const siteCount =
			newFilters.site && newFilters.site.length > 0
				? newFilters.site.length
				: 0;
		setFiltersNbr(siteCount);
		setPage(1);
	};

	const handleRowClick = (record: TProduction) => {
		const isAlreadySelected =
			selectedProductionsForMint.some(
				(item) => item.id === record.id
			);

		if (isAlreadySelected) {
			setSelectedProductionsForMint(
				selectedProductionsForMint.filter(
					(item) => item.id !== record.id
				)
			);
		} else {
			setSelectedProductionsForMint([
				...selectedProductionsForMint,
				record,
			]);
		}
	};

	const handleReset = () => {
		setIsMintProductions(false);
		setSelectedProductionsForMint([]);
		setSelectedProjectForProductionsMint(null);
		setSelectedProducerForProductionsMint(null);
		setCurrentStepMintProductions(0);
	};

	const handleNext = () => {
		const canProceed =
			currentStepMintProductions === 0 &&
			selectedProductionsForMint.length > 0;

		if (canProceed) {
			setCurrentStepMintProductions(1);
		} else {
			toggleMint();
		}
	};

	const handlePrevious = () => {
		if (currentStepMintProductions === 1) {
			setCurrentStepMintProductions(0);
		}
	};

	const handleCancel = () => {
		setIsOpenModalCancel(true);
	};

	const TotalTokenEquivalents = isArray(
		selectedProductionsForMint
	)
		? sum(
				selectedProductionsForMint.map((item) => {
					if (isNil(item.tokenEquivalentNumeric)) {
						return 0;
					}
					return item.tokenEquivalentNumeric;
				})
			)
		: 0;

	const totalCount = selectedProductionsForMint.length;

	const formattedTokens = new Intl.NumberFormat('fr-FR', {
		minimumFractionDigits: 3,
		maximumFractionDigits: 3,
	}).format(TotalTokenEquivalents);

	const isDateSelected = !!(
		queryParams.startDate?.$gte &&
		queryParams.startDate?.$lt
	);
	const handleDateChange = (
		value: [string | null, string | null]
	) => {
		const newFilters = cloneDeep(queryParams);

		if (
			!value ||
			(value[0] === null && value[1] === null)
		) {
			delete newFilters.startDate;
			setSelectedProductionsForMint([]);
		} else {
			newFilters.startDate = {
				$gte: value[0] ?? '',
				$lt: value[1] ?? '',
			};
		}
		handleApplyFilters(newFilters);
		setSelectedProductionsForMint([]);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Paper
			className={clsx(
				classes['main-layout'],
				currentStepMintProductions !== 0 &&
					classes['main-layout-step2']
			)}
		>
			<Filter
				opened={openedFilters}
				onClose={closeFilters}
				title={t('filters.titles.productions')}
				onApplyFilters={handleApplyFilters}
				value={queryParams}
			/>
			<Details
				opened={openedDetailsProduction}
				data={selectedProduction}
				onClose={() => {
					toggleDetailsProduction();
					setSelectedProduction(null);
				}}
				onVerify={() => {}}
			/>
			<DefaultModal
				opened={openedDeleteProduction}
				onConfirm={() => {
					if (selectedProduction)
						setSelectedProductionsForMint(
							selectedProductionsForMint.filter(
								(item) => item.id != selectedProduction.id
							)
						);
					closeDeleteProduction();
				}}
				onClose={() => {
					closeDeleteProduction();
					setSelectedProduction(null);
				}}
				title={t('productions.confirmDeleteProductions')}
				description={`${1} ${t('productions.productionWillBeDeleted')}. ${t('global.warningActionIrreversible')}`}
				type="warning"
			/>
			<DefaultModal
				opened={openedMint}
				onConfirm={() => {
					if (selectedProjectForProductionsMint)
						mintProductionMutation.mutate({
							project: selectedProjectForProductionsMint,
							productions: selectedProductionsForMint.map(
								(item) => item.id
							),
						});

					closeMint();
				}}
				onClose={closeMint}
				title={t('productions.confirmMintProductions')}
				description={`${t('global.warningActionIrreversible')}`}
				type="warning"
			/>
			<DefaultModal
				opened={isOpenModalCancel}
				onConfirm={() => {
					handleReset();
				}}
				onClose={() => {
					setIsOpenModalCancel(false);
				}}
				title={t('productions.backToAllProductionsSure')}
			/>
			<StepperHeader
				onCancel={handleCancel}
				onPrevious={handlePrevious}
				onNext={handleNext}
				activeStep={currentStepMintProductions}
			/>
			<Center mt={'12px'}>
				<Title c={'#9BA6B7'} order={6}>
					<Text span c={'#1D3965'} inherit>
						{`${totalCount} `}
					</Text>
					{totalCount <= 1
						? t(
								'productions.productionReadyToMintWithAnEquivalentOf'
							)
						: t(
								'productions.productionsReadyToMintWithAnEquivalentOf'
							)}
					<Text span c={'#1D3965'} inherit>
						{` ${formattedTokens} tCO2e`}
					</Text>
				</Title>
			</Center>
			{currentStepMintProductions === 0 ? (
				<>
					<Center>
						<Flex
							gap="24px"
							justify="flex-start"
							align="end"
							wrap="wrap"
							mt={'12px'}
						>
							<Select
								w="320px"
								searchable
								size="md"
								label={t('global.producer')}
								withAsterisk
								data={convertArrayToLabelValue(
									producers?.rows
								)}
								value={selectedProducerForProductionsMint}
								onChange={handleProducerChange}
								placeholder={t(
									'placeholders.selectProducer'
								)}
							/>
							<Select
								w="320px"
								searchable
								size="md"
								disabled={
									!selectedProducerForProductionsMint
								}
								label={t('global.project')}
								withAsterisk
								data={convertArrayToLabelValue(
									projectsFiltred
								)}
								value={selectedProjectForProductionsMint}
								placeholder={t(
									'placeholders.selectProject'
								)}
								onChange={handleProjectChange}
							/>
							<DatePickerInput
								disabled={
									!selectedProjectForProductionsMint
								}
								getDayProps={(date) => {
									const start = queryParams.startDate
										?.$gte
										? new Date(
												queryParams.startDate
													.$gte as string
											)
										: null;

									const end = queryParams.startDate?.$lt
										? new Date(
												queryParams.startDate
													.$lt as string
											)
										: null;

									if (start && end) {
										return {};
									}

									if (start) {
										const diff = dayjs(date).diff(
											dayjs(start),
											'day'
										);

										if (Math.abs(diff) > 29) {
											return {
												disabled: true,
												style: {
													color:
														'var(--mantine-color-gray-4)',
													opacity: 0.5,
													cursor: 'not-allowed',
												},
											};
										}
									}

									return {};
								}}
								w="320px"
								value={[
									queryParams.startDate?.$gte
										? new Date(
												queryParams.startDate
													.$gte as string
											)
										: null,
									queryParams.startDate?.$lt
										? new Date(
												queryParams.startDate
													.$lt as string
											)
										: null,
								]}
								onChange={handleDateChange}
								size="md"
								label={'Date'}
								withAsterisk
								placeholder={t('filters.titles.perDate')}
								clearable
								type="range"
								presets={[
									{
										value: [
											dayjs().startOf('week').toDate(),
											dayjs().endOf('week').toDate(),
										],
										label: 'This week',
									},
									{
										value: [
											dayjs().startOf('month').toDate(),
											dayjs().endOf('month').toDate(),
										],
										label: 'This month',
									},
									{
										value: [
											dayjs().startOf('year').toDate(),
											dayjs().endOf('year').toDate(),
										],
										label: 'This year',
									},
								]}
							/>
							<div style={{ position: 'relative' }}>
								{queryParams.site && (
									<Badge
										style={{
											position: 'absolute',
											zIndex: '3',
											right: '-3px',
											fontFamily: 'InterDisplay-Regular',
										}}
										color="var(--mantine-color-igluRed-9)"
										size="xs"
										circle
									>
										{1}
									</Badge>
								)}

								<ActionIcon
									variant="outline"
									size="48px"
									radius="xl"
									color="var(--mantine-color-igluBorder-9)"
									data-testid="bell-icon-button"
									onClick={openFilters}
									bg={
										queryParams?.site
											? 'var(--mantine-color-igluPrimary-9)'
											: 'transparent'
									}
								>
									<IconFilter
										color={
											queryParams?.site
												? 'var(--mantine-color-igluTextButton-9)'
												: 'var(--mantine-color-igluPrimary-9)'
										}
										radius="xl"
										size={24}
										stroke={1.5}
									/>
								</ActionIcon>
							</div>
						</Flex>
					</Center>
					<Space h="24px" />
					<Box className={classes['container-table']}>
						<Datatable
							height={'90%'}
							fetching={isFetching}
							records={
								!!selectedProjectForProductionsMint
									? data?.rows
									: []
							}
							columns={addFooterProductionsForMintColumn(
								data?.rows,
								columns
							)}
							onShow={(row: TProduction) => {
								setSelectedProduction(row);
								toggleDetailsProduction();
							}}
							selectedRecords={
								isDateSelected
									? selectedProductionsForMint
									: null
							}
							onSelectedRecordsChange={
								isDateSelected
									? setSelectedProductionsForMint
									: null
							}
							onRowClick={({
								record,
							}: {
								record: TProduction;
							}) => {
								if (isDateSelected)
									handleRowClick(record as TProduction);
							}}
						/>
						<Pagination
							activePage={page}
							pageSize={pageSize}
							totalItems={0}
							total={data?.totalPages ?? 0}
							onChange={setPage}
							onPageSizeChange={(s) => {
								setPageSize(Number(s));
								setPage(1);
							}}
							maxPageSize={999}
						/>
					</Box>
				</>
			) : (
				<Box mt="xl">
					<Box className={classes['container-table']}>
						<Datatable
							height={'90%'}
							scrollAreaProps={{
								type: 'always',
								offsetScrollbars: true,
							}}
							records={selectedProductionsForMint}
							columns={columnsReviews}
							onDelete={(e: TProduction) => {
								setSelectedProduction(e);
								toggleDeleteProduction();
							}}
							onShow={(row: TProduction) => {
								setSelectedProduction(row);
								toggleDetailsProduction();
							}}
						/>
						<Space h={'md'} />
					</Box>
				</Box>
			)}
		</Paper>
	);
};

export default MintProductions;
